import { useRef, FC, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { updateContentElementById, selectDocumentType, DocumentType } from 'features/document/documentSlice';
import { DraggableComponentProps } from 'features/document/constants';
import { isTextEditorElement } from 'utils/typeGuards';
import { selectContract, selectQuote, selectLoan } from 'features/salesforce/salesforceSlice';
import './TextEditor.css';
import { Contract, Quote, Loan } from 'features/salesforce/salesforceTypes';
import { debounce } from 'lodash';
import { Box } from '@mui/material';

const getVariablesMapper = (variablesObject: Contract | Quote | Loan) => {
  if (!variablesObject) return;
  return Object.fromEntries(
    Object.entries(variablesObject).filter(variable => variable[1] !== null && variable[1] !== '')
  );
};
interface TextEditorProps extends DraggableComponentProps {
  isViewMode?: boolean;
}

const TextEditor: FC<TextEditorProps> = ({ currentElement, isViewMode }) => {
  if (!isTextEditorElement(currentElement)) {
    return null;
  }

  const editorRef = useRef<TinyMCEEditor | null>(null);
  const dispatch = useAppDispatch();
  const id = uuidv4();
  const [value, setValue] = useState(currentElement.content.value);

  const { paddingX, paddingY } = currentElement.content;
  const paddingString = `${paddingY || 8}px ${paddingX || 12}px`;

  const contract = useAppSelector(selectContract) || {};
  const quote = useAppSelector(selectQuote) || {};
  const loan = useAppSelector(selectLoan) || {};

  const variablesSource = {
    [DocumentType.CONTRACT]: { value: contract, key: contract.ContractNumber },
    [DocumentType.QUOTE]: { value: quote, key: quote.Id },
    [DocumentType.PRECLOSING]: { value: loan, key: loan.Id },
  };
  const documentType = useAppSelector(selectDocumentType);
  const variableMapper = useMemo(() => {
    return getVariablesMapper(variablesSource[documentType as keyof typeof variablesSource]?.value);
  }, [contract, quote, loan]);

  const key = variablesSource[documentType as keyof typeof variablesSource]?.key;

  const handleChange = (newValue: string) => {
    setValue(newValue);
    debounceFn(newValue);
  };

  const debounceFn = debounce(newValue => {
    dispatch(
      updateContentElementById({
        content: {
          value: newValue,
        },
      })
    );
  }, 500);

  return (
    <Box padding={paddingString}>
      <Editor
        key={key}
        apiKey='d1ppt3k9ktqwk7zntqh64w2jp25sdskqasfs0pala8h3k737'
        id={id}
        onInit={(evt, editor) => (editorRef.current = editor)}
        value={value}
        init={{
          /* eslint-disable  @typescript-eslint/no-explicit-any */
          table_responsive_width: true,
          table_default_styles: { width: '-webkit-fill-available', 'border-collapse': 'collapse' },
          selector: `#${id}` as any,
          font_family_formats:
            'Arial=arial,helvetica,sans-serif;Calibri=calibri-light; Courier New=courier new,courier;Garamond=garamond; Georgia=georgia,palatino; Helvetica=helvetica; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;',
          external_plugins: {
            variable: '/plugin.js',
          },
          content_style:
            "@import url('https://fonts.cdnfonts.com/css/garamond');@import url('https://fonts.cdnfonts.com/css/calibri-light');",
          plugins: 'table charmap lists advlist directionality  code  media ',
          variable_mapper: variableMapper,
          variable_prefix: '[',
          variable_suffix: ']',
          placeholder: 'Add some text',
          font_size_formats:
            '5pt 6pt 7pt 8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 26pt 28pt 36pt 48pt 72pt',
          toolbar:
            'undo redo fontsize  | blocks  | table | fontfamily | ' +
            'bold italic underline forecolor charmap | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat media importword ltr rtl code',
          inline: true,
          menubar: false,
          max_width: 100,
          width: '100%',
          fixed_toolbar_container: '#myToolbarContainer',
          content_css: './TextEditor.css',
        }}
        disabled={isViewMode}
        onEditorChange={handleChange}
      />
    </Box>
  );
};

export default TextEditor;
